import { useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import Layout from "./components/Layout";

import Dashboard from "./components/dashboard";
import WorkOrderUI from "./components/workOrder";
import Analytics from "./components/Analytics";
import SignIn from "./components/SignIn";
import ObjectivesCriteria from "./components/ObjectivesCriteria";
import Alerts from "./components/Alerts";
import OnHold from "./components/OnHold";
import Transfers from "./components/Transfers";
import ProductList from "./components/Products";
import AceLynkGenerator from "./components/AceLynkGenerator";
import WeeklyPlanner from './components/WeeklyPlanner';
import ProductsAssignedValue from "./components/ProductsAssignedValue";
import CostingTool from "./components/CostingTool";
import CostingSettings from "./components/CostingSettings";
import GeneratedUPCsView from "./components/UPCTableViewer";
import CustomerShippingSchedule from "./components/CustomerShippingSchedule";
import PriceManagement from "./components/PriceManagement";
import AmazonOrders from "./components/AmazonOrders";
import WalmartOrders from "./components/WalmartOrders";
import AdminSettings from "./data-governance-layer/AdminSettings";
import AnalyticsSalesDashboard from "./components/AnalyticsSalesDashboard";
import SalesERPag from "./components/SalesERPag";
import QuotationCenter from "./components/QuotationCenter";
// Removed AutomatedPOImport route

// import ShopifyOrders from "./components/ShopifyOrders";

import BomDb from "./components/BomDbUpload";
import Onboarding from "./components/Onboarding";
import ForgotPassword from "./components/ForgotPassword";
import UserAndPermissionsManagement from "./components/UserAndPermissionsManagement";
import NoModuleAccess from "./components/NoModuleAccess";

const Placeholder = ({ title }) => (
  <div className="p-6">
    <div className="bg-white rounded-lg shadow p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">{title}</h1>
      <p className="text-gray-600">This module is under development and will be available soon.</p>
    </div>
  </div>
);

const ProtectedModuleRoute = ({
  modulePermission,
  pagePermission,
  children,
  fallback = "/no-access",
}) => {
  const { canRead } = useContext(AuthContext);

  if (!canRead(modulePermission) || !canRead(pagePermission)) {
    return <Navigate to={fallback} replace />;
  }

  return children;
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, user, permissionsLoading, canRead } =
    useContext(AuthContext);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (user?.mustChangePassword === true || !user?.isSetupComplete) {
    return <Navigate to="/onboarding" replace />;
  }

if (permissionsLoading) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 px-10 py-12 text-center max-w-sm w-full">

        {/* Lock Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-14 h-14 rounded-full border-4 border-gray-200"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="animate-spin h-8 w-8 text-black"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-20"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-80"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Verifying Access
        </h2>

        {/* Subtitle */}
        <p className="text-sm text-gray-500">
          Please wait while we securely validate your permissions.
        </p>

      </div>
    </div>
  );
}

  const hasAnyModule =
    canRead("AccessERPag") ||
    canRead("AccessAceLynk") ||
    canRead("AccessCosting") ||
    canRead("AccessUserPermissionManagement") ||
    canRead("AccessCompanyDashboard") ||
    canRead("AccessSales");

  if (!hasAnyModule) {
    return <NoModuleAccess />;
  }
  return children;
};

const HomeRedirect = () => {
  const { canRead } = useContext(AuthContext);
  if (canRead("AccessERPag") && canRead("ERPagDashboard")) {
    return <Navigate to="/dashboard" replace />;
  }
  if (canRead("AccessAceLynk") && canRead("AceLynkXML")) {
    return <Navigate to="/acelynk" replace />;
  }
  if (canRead("AccessCosting") && canRead("CostingToolPage")) {
    return <Navigate to="/costing-tool" replace />;
  }
  if (
    canRead("AccessUserPermissionManagement") &&
    canRead("UsersPermissionsPage")
  ) {
    return <Navigate to="/UserAndPermissionsManagement" replace />;
  }
  if (canRead("AccessCompanyDashboard") && canRead("CompanyDashboard")) {
    return <Navigate to="/company-dashboard" replace />;
  }
  if (canRead("AccessSales") && canRead("SalesERPag")) {
    return <Navigate to="/sales-erpag" replace />;
  }
  return <Navigate to="/no-access" replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            {/* ERPag Module */}
            <Route
              path="dashboard"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagDashboard"
                >
                  <Dashboard />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="workorder"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagWorkOrder"
                >
                  <WorkOrderUI />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="alerts"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagAlerts"
                >
                  <Alerts />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagAnalytics"
                >
                  <Analytics />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="on-hold"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagOnHold"
                >
                  <OnHold />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="transfers"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagTransfers"
                >
                  <Transfers />
                </ProtectedModuleRoute>
              }
            />
            <Route path="no-access" element={<NoModuleAccess />} />
            <Route path="" element={<HomeRedirect />} />
            <Route
              path="products"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagProducts"
                >
                  <ProductList />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="products-settings"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagAssignedValues"
                >
                  <ProductsAssignedValue />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="amazon-orders"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="AccessERPag"
                >
                  <AmazonOrders />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="walmart-orders"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="AccessERPag"
                >
                  <WalmartOrders />
                </ProtectedModuleRoute>
              }
            />
            {/*
            <Route
              path="shopify-orders"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="AccessERPag"
                >
                  <ShopifyOrders />
                </ProtectedModuleRoute>
              }
            /> 
            */}           
            <Route
              path="customer-shipping-schedule"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagCustomerShippingSchedule"
                >
                  <CustomerShippingSchedule />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="UserAndPermissionsManagement"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessUserPermissionManagement"
                  pagePermission="UsersPermissionsPage"
                >
                  <UserAndPermissionsManagement />
                </ProtectedModuleRoute>
              }
            />

            {/* AceLynk Module */}
            <Route
              path="acelynk"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessAceLynk"
                  pagePermission="AceLynkXML"
                >
                  <AceLynkGenerator />
                </ProtectedModuleRoute>
              }
            />

            {/* AceLynk Module */}
            <Route
              path="weekly-planner"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessERPag"
                  pagePermission="ERPagCustomerShippingSchedule"
                >
                  <WeeklyPlanner />
                </ProtectedModuleRoute>
              }
            />            

            {/* Costing Tool Module */}
            <Route
              path="costing-tool"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessCosting"
                  pagePermission="CostingToolPage"
                >
                  <CostingTool />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="quotation-center"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessCosting"
                  pagePermission="CostingToolPage"
                >
                  <QuotationCenter />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="generated-upcs"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessCosting"
                  pagePermission="CostingGeneratedUPCs"
                >
                  <GeneratedUPCsView />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="costing-settings"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessCosting"
                  pagePermission="CostingSettingsPage"
                >
                  <CostingSettings />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="price-management"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessCosting"
                  pagePermission="CostingToolPage"
                >
                  <PriceManagement />
                </ProtectedModuleRoute>
              }
            />

            {/* Company Dashboard Module */}
            <Route
              path="company-dashboard"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessCompanyDashboard"
                  pagePermission="CompanyDashboard"
                >
                  <Placeholder title="Company Dashboard" />
                </ProtectedModuleRoute>
              }
            />

            {/* Sales Module */}

            <Route
              path="admin-settings"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessSales"
                  pagePermission="SalesAnalytics"
                >
                  <AdminSettings />
                </ProtectedModuleRoute>
              }
            />            
            <Route
              path="sales-analytics"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessSales"
                  pagePermission="SalesAnalytics"
                >
                  <AnalyticsSalesDashboard />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="sales-erpag"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessSales"
                  pagePermission="SalesERPag"
                >
                  <SalesERPag />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="sales-amazon"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessSales"
                  pagePermission="SalesAmazon"
                >
                  <AmazonOrders />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="sales-shopify"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessSales"
                  pagePermission="SalesShopify"
                >
                  <Placeholder title="Sales Shopify" />
                </ProtectedModuleRoute>
              }
            />
            <Route
              path="sales-walmart"
              element={
                <ProtectedModuleRoute
                  modulePermission="AccessSales"
                  pagePermission="SalesWalmart"
                >
                  <WalmartOrders />
                </ProtectedModuleRoute>
              }
            />

            {/* Catch-all */}
            <Route
              path="*"
              element={
                <PrivateRoute>
                  {" "}
                  <NoModuleAccess />
                </PrivateRoute>
              }
            />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
